// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

// eslint-disable-next-line spaced-comment
/// <reference types="@types/google.analytics" />

const defaultRetries = 2;
const defaultRetryDelayMs = 250;
const retryableStatuses = [408, 429, 500, 502, 503, 504];

export interface RetryOptions {
    retries?: number;
    retryDelayMs?: number;
}

export function defaultGetOptions(signal?: AbortSignal): RequestInit {
    const headers: HeadersInit = new Headers();
    headers.set('Content-Type', 'application/json');

    const options: RequestInit = {
        method: 'GET',
        headers: headers,
    };

    if (signal) {
        options.signal = signal;
    }

    if (window.ga) {
        window.ga((tracker: UniversalAnalytics.Tracker | undefined) => {
            headers.set('Protolabs-Analytics-Id', tracker?.get('clientId'));
        });
    }

    return options;
}

export function defaultHeadOptions(signal?: AbortSignal): RequestInit {
    return { ...defaultGetOptions(signal), ...{ method: 'HEAD' } };
}

export function defaultDeleteOptions(signal?: AbortSignal): RequestInit {
    return { ...defaultGetOptions(signal), ...{ method: 'DELETE' } };
}

export function defaultPatchOptions(
    payload = {},
    signal?: AbortSignal
): RequestInit {
    const additionalOptions = {
        method: 'PATCH',
        body: JSON.stringify(payload),
    };

    return { ...defaultGetOptions(signal), ...additionalOptions };
}

export function defaultPutOptions(
    payload = {},
    signal?: AbortSignal
): RequestInit {
    const additionalOptions = {
        method: 'PUT',
        body: JSON.stringify(payload),
    };

    return { ...defaultGetOptions(signal), ...additionalOptions };
}

export function defaultPostOptions(
    payload = {},
    signal?: AbortSignal
): RequestInit {
    const additionalOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify(payload),
        credentials: 'include',
    };

    return { ...defaultGetOptions(signal), ...additionalOptions };
}

export function getErrorMessage(error: unknown): string {
    let errorMessage = 'Unknown error';

    if (error instanceof Error) {
        errorMessage = error.message;
    } else {
        errorMessage = String(error);
    }

    return errorMessage;
}

export function isAbortError(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        (error as { name?: string }).name === 'AbortError'
    );
}

export function logErrorToConsole(error: unknown): void {
    if (isAbortError(error)) {
        return;
    }

    console.error(getErrorMessage(error));
}

function createAbortError(): Error {
    return new DOMException('The operation was aborted.', 'AbortError');
}

function delayAsync(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(createAbortError());
            return;
        }

        const timeoutId = setTimeout(resolve, ms);

        signal?.addEventListener(
            'abort',
            () => {
                clearTimeout(timeoutId);
                reject(createAbortError());
            },
            { once: true }
        );
    });
}

/**
 * Issues a GET and retries transient network failures and 408/429/5xx
 * responses using an exponential backoff. Aborts are never retried; the
 * AbortError is rethrown so callers can tell a cancelled request apart from a
 * failed one.
 */
export async function fetchGetWithRetryAsync(
    url: string,
    options: RequestInit = {},
    retryOptions: RetryOptions = {}
): Promise<Response> {
    const retries = retryOptions.retries ?? defaultRetries;
    const retryDelayMs = retryOptions.retryDelayMs ?? defaultRetryDelayMs;
    const requestOptions: RequestInit = { ...options, method: 'GET' };
    const signal = requestOptions.signal ?? undefined;

    let lastResponse: Response | null = null;
    let lastError: unknown = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
        if (attempt > 0) {
            await delayAsync(retryDelayMs * 2 ** (attempt - 1), signal);
        }

        if (signal?.aborted) {
            throw createAbortError();
        }

        try {
            const response = await fetch(url, requestOptions);

            if (!retryableStatuses.includes(response.status)) {
                return response;
            }

            lastResponse = response;
            lastError = null;
        } catch (error) {
            if (isAbortError(error)) {
                throw error;
            }

            lastResponse = null;
            lastError = error;
        }
    }

    if (lastResponse) {
        return lastResponse;
    }

    throw lastError;
}

export default {
    defaultFetchOptions: defaultGetOptions,
    defaultHeadOptions,
    defaultDeleteOptions,
    defaultPatchOptions,
    defaultPutOptions,
    defaultPostOptions,
    fetchGetWithRetryAsync,
    isAbortError,
    logErrorToConsole,
    getErrorMessage,
};
